import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useTranslation } from 'react-i18next';
import { ACCOUNTING_API, InventorySummaryQueryParams } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';

interface IdleProduct {
  id: string;
  name: string;
  stockCode?: string | null;
  barcode?: string | null;
  unit?: string | null;
  categoryName?: string | null;
  purchasePrice: number;
  salePrice: number;
  stockQuantity: number;
  stockValue: number;
  lastMovementDate?: string | null;
}

type SortBy = 'lastMovement' | 'stockValue' | 'name';

interface IdleProductsDialogProps {
  open: boolean;
  onClose: () => void;
  filters?: InventorySummaryQueryParams;
}

const formatCurrency = (value: number) =>
  value.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 });

const formatQuantity = (value: number) =>
  value.toLocaleString('tr-TR', { maximumFractionDigits: 3 });

const IdleProductsDialog: React.FC<IdleProductsDialogProps> = ({ open, onClose, filters }) => {
  const { t } = useTranslation('accounting');
  const [items, setItems] = useState<IdleProduct[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [idleDays, setIdleDays] = useState(90);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('lastMovement');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    setPage(0);
    setSearchInput('');
    setSearch('');
    setSortBy('lastMovement');
  }, [open]);

  useEffect(() => {
    // PERFORMANS: tek karakterle arama yapılmaz
    if (searchInput.trim().length === 1) return undefined;
    const handle = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    if (!open) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    accountingApi
      .get(ACCOUNTING_API.INVENTORY_IDLE_PRODUCTS(filters, {
        page: page + 1,
        pageSize,
        search: search || undefined,
        sortBy,
      }))
      .then(response => {
        if (requestId !== requestIdRef.current) return;
        const data = response.data?.data ?? response.data;
        setItems(data?.items ?? []);
        setTotalCount(data?.totalCount ?? 0);
        setIdleDays(data?.idleDays ?? 90);
      })
      .catch(err => {
        if (requestId !== requestIdRef.current) return;
        console.error('❌ [INVENTORY] Hareketsiz ürünler yüklenemedi:', err);
        setError(t('stock.idleDialog.loadError'));
        setItems([]);
        setTotalCount(0);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [open, filters, page, pageSize, search, sortBy, t]);

  const formatLastMovement = (value?: string | null) => {
    if (!value) return <Chip size="small" color="error" variant="outlined" label={t('stock.idleDialog.neverMoved')} />;
    const date = new Date(value);
    const days = Math.floor((Date.now() - date.getTime()) / 86400000);
    return (
      <Box>
        <Typography variant="body2">{date.toLocaleDateString('tr-TR')}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t('stock.idleDialog.daysAgo', { count: days })}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <HourglassEmptyIcon color="error" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="span">
            {t('stock.idleDialog.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('stock.idleDialog.subtitle', { days: idleDays })}
          </Typography>
        </Box>
        <Chip label={t('stock.idleDialog.total', { count: totalCount })} color="error" variant="outlined" />
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder={t('stock.idleDialog.searchPlaceholder')}
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>{t('stock.idleDialog.sortBy')}</InputLabel>
            <Select
              value={sortBy}
              label={t('stock.idleDialog.sortBy')}
              onChange={e => {
                setSortBy(e.target.value as SortBy);
                setPage(0);
              }}
            >
              <MenuItem value="lastMovement">{t('stock.idleDialog.sort.lastMovement')}</MenuItem>
              <MenuItem value="stockValue">{t('stock.idleDialog.sort.stockValue')}</MenuItem>
              <MenuItem value="name">{t('stock.idleDialog.sort.name')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer sx={{ position: 'relative', minHeight: 200 }}>
          {loading && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.6)', zIndex: 1 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t('stock.idleDialog.columns.product')}</TableCell>
                <TableCell>{t('stock.idleDialog.columns.category')}</TableCell>
                <TableCell align="right">{t('stock.idleDialog.columns.quantity')}</TableCell>
                <TableCell align="right">{t('stock.idleDialog.columns.purchasePrice')}</TableCell>
                <TableCell align="right">{t('stock.idleDialog.columns.salePrice')}</TableCell>
                <TableCell align="right">{t('stock.idleDialog.columns.stockValue')}</TableCell>
                <TableCell>{t('stock.idleDialog.columns.lastMovement')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && items.length === 0 && !error && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    {t('stock.idleDialog.empty')}
                  </TableCell>
                </TableRow>
              )}
              {items.map(item => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[item.stockCode, item.barcode].filter(Boolean).join(' • ') || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.categoryName || '-'}</TableCell>
                  <TableCell align="right">
                    {formatQuantity(item.stockQuantity)} {item.unit || ''}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(item.purchasePrice)}</TableCell>
                  <TableCell align="right">{formatCurrency(item.salePrice)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>{formatCurrency(item.stockValue)}</Typography>
                  </TableCell>
                  <TableCell>{formatLastMovement(item.lastMovementDate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={e => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t('stock.pagination.rowsPerPage')}
        />
      </DialogContent>
    </Dialog>
  );
};

export default IdleProductsDialog;
